import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { PageHero } from "@/components/sections/PageHero";
import { LeadSection } from "@/components/sections/LeadSection";
import { TotalSolution } from "@/components/sections/TotalSolution";
import { WhyBrewer } from "@/components/sections/WhyBrewer";
import { EASE, Reveal, SectionHead } from "@/components/motion/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { IMG, PARTNER_STEPS, PARTNER_FIT, PARTNER_FAQ } from "@/data/site";

const Steps = () => (
  <section className="py-28 lg:py-36" data-testid="partner-steps-section">
    <div className="container-x">
      <SectionHead index="01" eyebrow="Revenue Share" title={["You provide the space.", "We handle the rest."]}>
        A revenue share partnership lets you add entertainment without buying equipment. Brewer supplies and services the machines, and the revenue is shared.
      </SectionHead>
      <div className="mt-16 grid gap-5 md:grid-cols-3">
        {PARTNER_STEPS.map((s, i) => (
          <motion.div key={s.n} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, ease: EASE, delay: i * 0.12 }} className="relative rounded-[24px] border border-white/5 bg-ink-800 p-8 [--outline-fill:#121722]" data-testid={`partner-step-${i + 1}`}>
            <p className="font-display text-7xl font-black leading-none tracking-tighter text-outline">{s.n}</p>
            <h3 className="mt-8 font-display text-2xl font-semibold text-white">{s.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Fit = () => (
  <section className="border-t border-white/5 py-28 lg:py-36" data-testid="partner-fit-section">
    <div className="container-x grid items-center gap-14 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <div className="relative h-[380px] overflow-hidden rounded-[28px] border border-white/5 lg:h-[520px]">
          <img src={IMG.handshake} alt="Business partnership handshake" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-ink-950/70 via-transparent to-volt/10" />
        </div>
      </Reveal>
      <div className="lg:col-span-5 lg:col-start-8">
        <SectionHead index="02" eyebrow="Is it a fit?" title={["Great partners", "usually have:"]} className="lg:grid-cols-1" />
        <ul className="mt-10 space-y-5">
          {PARTNER_FIT.map((f, i) => (
            <Reveal key={f} delay={i * 0.08}>
              <li className="flex items-start gap-4 border-b border-white/5 pb-5 text-base text-slate-200">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-volt text-ink-950"><Check className="h-3.5 w-3.5" /></span> {f}
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const Faq = () => (
  <section className="border-t border-white/5 py-28 lg:py-36">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-4"><SectionHead index="05" eyebrow="FAQ" title={["Partnership", "questions"]} className="lg:grid-cols-1" /></div>
      <Accordion type="single" collapsible className="lg:col-span-7 lg:col-start-6">
        {PARTNER_FAQ.map(([q, a], i) => (
          <AccordionItem key={q} value={`p${i}`} className="border-white/10">
            <AccordionTrigger className="py-6 text-left font-display text-lg text-white hover:no-underline" data-testid={`partner-faq-trigger-${i + 1}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-base leading-relaxed text-slate-400">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export default function Partner() {
  return (
    <div data-testid="partner-page">
      <PageHero testId="partner-hero" eyebrow="Partner With Brewer" lines={["Your Space.", "Our Machines."]} img={IMG.cabinetRow} sub="Add arcade games, prize machines, jukeboxes, pool tables and ATMs to your business with a revenue share partnership — or purchase outright. Brewer installs and supports it all." />
      <Steps />
      <Fit />
      <TotalSolution index="03" />
      <WhyBrewer index="04" />
      <Faq />
      <LeadSection index="06" source="partner-page" interest="Revenue Share Partnership" />
    </div>
  );
}
